/**
 * Color Palette Definitions
 * Four industry-standard palettes that can be switched at runtime
 */

export interface ColorPalette {
  name: string;
  description: string;
  primary: string;
  primaryDark: string;
  primaryLight: string;
  secondary: string;
  success: string;
  error: string;
  warning: string;
  info: string;
  background: string;
  paper: string;
}

export const COLOR_PALETTES: Record<string, ColorPalette> = {
  etg: {
    name: 'ETG Original',
    description: 'Original Executive Talents Group colors (Blue & Red)',
    primary: '#1a3c8f',
    primaryDark: '#0f2454',
    primaryLight: '#4a68c4',
    secondary: '#cc2229',
    success: '#059669',
    error: '#dc2626',
    warning: '#ea580c',
    info: '#0284c7',
    background: '#f5f6fa',
    paper: '#ffffff',
  },

  fintech: {
    name: 'FinTech / Banking',
    description: 'Trust, security, professional (Stripe, Wise, Square)',
    primary: '#1e40af',
    primaryDark: '#1e3a8a',
    primaryLight: '#93c5fd',
    secondary: '#3b82f6',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#0ea5e9',
    background: '#f0f9ff',
    paper: '#ffffff',
  },

  saas: {
    name: 'Modern SaaS / Tech',
    description: 'Clean, minimal, innovation (Figma, Notion, Linear)',
    primary: '#4f46e5',
    primaryDark: '#4f46e5',
    primaryLight: '#c7d2fe',
    secondary: '#818cf8',
    success: '#14b8a6',
    error: '#f43f5e',
    warning: '#f59e0b',
    info: '#06b6d4',
    background: '#f5f3ff',
    paper: '#ffffff',
  },

  enterprise: {
    name: 'Enterprise ERP',
    description: 'Serious, data-focused (SAP, Oracle, NetSuite)',
    primary: '#1e293b',
    primaryDark: '#0f172a',
    primaryLight: '#cbd5e1',
    secondary: '#475569',
    success: '#0891b2',
    error: '#dc2626',
    warning: '#ea580c',
    info: '#0284c7',
    background: '#f1f5f9',
    paper: '#ffffff',
  },

  nigeria: {
    name: 'Nigeria B2B / Fintech',
    description: 'Vibrant, accessible, local (Paystack, Flutterwave)',
    primary: '#d97706',
    primaryDark: '#b45309',
    primaryLight: '#fed7aa',
    secondary: '#f97316',
    success: '#059669',
    error: '#dc2626',
    warning: '#ea580c',
    info: '#0284c7',
    background: '#fffbeb',
    paper: '#ffffff',
  },

  ocean: {
    name: 'Ocean Breeze',
    description: 'Calm, modern, trustworthy (Vercel, Supabase)',
    primary: '#0d9488',
    primaryDark: '#115e59',
    primaryLight: '#99f6e4',
    secondary: '#06b6d4',
    success: '#22c55e',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
    background: '#f0fdfa',
    paper: '#ffffff',
  },

  sunset: {
    name: 'Sunset Coral',
    description: 'Warm, creative, energetic (Linear, Loom)',
    primary: '#e11d48',
    primaryDark: '#9f1239',
    primaryLight: '#fda4af',
    secondary: '#f97316',
    success: '#10b981',
    error: '#dc2626',
    warning: '#eab308',
    info: '#6366f1',
    background: '#fff1f2',
    paper: '#ffffff',
  },

  forest: {
    name: 'Forest Green',
    description: 'Growth, stability, nature (Shopify, Robinhood)',
    primary: '#16a34a',
    primaryDark: '#14532d',
    primaryLight: '#bbf7d0',
    secondary: '#059669',
    success: '#0d9488',
    error: '#e11d48',
    warning: '#ea580c',
    info: '#0ea5e9',
    background: '#f0fdf4',
    paper: '#ffffff',
  },

  midnight: {
    name: 'Midnight Violet',
    description: 'Premium, bold, creative (Arc, Framer)',
    primary: '#7c3aed',
    primaryDark: '#4c1d95',
    primaryLight: '#c4b5fd',
    secondary: '#a855f7',
    success: '#10b981',
    error: '#f43f5e',
    warning: '#f59e0b',
    info: '#06b6d4',
    background: '#faf5ff',
    paper: '#ffffff',
  },

  rosegold: {
    name: 'Rose Gold',
    description: 'Elegant, soft, refined (Airbnb, Dribbble)',
    primary: '#be185d',
    primaryDark: '#831843',
    primaryLight: '#fbcfe8',
    secondary: '#e879a8',
    success: '#059669',
    error: '#dc2626',
    warning: '#d97706',
    info: '#0284c7',
    background: '#fdf2f8',
    paper: '#ffffff',
  },

  slate: {
    name: 'Slate Minimal',
    description: 'Clean, focused, developer-friendly (GitHub, Raycast)',
    primary: '#334155',
    primaryDark: '#0f172a',
    primaryLight: '#94a3b8',
    secondary: '#3b82f6',
    success: '#22c55e',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#06b6d4',
    background: '#f8fafc',
    paper: '#ffffff',
  },

  copper: {
    name: 'Copper Earth',
    description: 'Warm, sophisticated, grounded (Squarespace, Aesop)',
    primary: '#92400e',
    primaryDark: '#78350f',
    primaryLight: '#fde68a',
    secondary: '#b45309',
    success: '#16a34a',
    error: '#dc2626',
    warning: '#ea580c',
    info: '#0891b2',
    background: '#fffbeb',
    paper: '#ffffff',
  },

  kova: {
    name: 'Kova',
    description: 'Premium emerald & warm orange — from the Kova marketing site',
    primary: '#0B6E4F',
    primaryDark: '#084A35',
    primaryLight: '#E8F5EE',
    secondary: '#E8590C',
    success: '#0B6E4F',
    error: '#C62828',
    warning: '#E8590C',
    info: '#2563EB',
    background: '#FAFBFC',
    paper: '#FFFFFF',
  },
};

/* ── Sidebar color scheme helper ── */

export interface SidebarColors {
  bg: string;
  text: string;
  textSecondary: string;
  activeText: string;
  activeBg: string;
  divider: string;
  hoverBg: string;
  sectionLabel: string;
  /** Use light icons/logos when bg is dark */
  isInverted: boolean;
}

/**
 * Compute sidebar colors for a given palette & style.
 *   light   → default (paper bg, standard text)
 *   colored → palette primaryDark bg, white text
 *   dark    → always dark bg, light text
 */
export function getSidebarColors(
  palette: ColorPalette,
  style: 'light' | 'colored' | 'dark',
  isDarkMode: boolean,
): SidebarColors {
  // In dark mode, sidebar is always dark regardless of chosen style
  if (isDarkMode) {
    return {
      bg: '#1A1C25',
      text: '#E4E6EB',
      textSecondary: '#9CA3AF',
      activeText: palette.primaryLight,
      activeBg: 'rgba(255,255,255,0.08)',
      divider: '#2D303A',
      hoverBg: 'rgba(255,255,255,0.05)',
      sectionLabel: '#6B7280',
      isInverted: true,
    };
  }

  switch (style) {
    case 'colored':
      return {
        bg: palette.primaryDark,
        text: 'rgba(255,255,255,0.92)',
        textSecondary: 'rgba(255,255,255,0.6)',
        activeText: '#FFFFFF',
        activeBg: 'rgba(255,255,255,0.14)',
        divider: 'rgba(255,255,255,0.12)',
        hoverBg: 'rgba(255,255,255,0.08)',
        sectionLabel: 'rgba(255,255,255,0.45)',
        isInverted: true,
      };

    case 'dark':
      return {
        bg: '#1A1C25',
        text: '#E4E6EB',
        textSecondary: '#9CA3AF',
        activeText: palette.primaryLight,
        activeBg: 'rgba(255,255,255,0.08)',
        divider: '#2D303A',
        hoverBg: 'rgba(255,255,255,0.05)',
        sectionLabel: '#6B7280',
        isInverted: true,
      };

    case 'light':
    default:
      return {
        bg: palette.paper,
        text: '#1A1A2E',
        textSecondary: '#6B7280',
        activeText: palette.primary,
        activeBg: `${palette.primary}14`, // ~8% opacity
        divider: '#E8EAF0',
        hoverBg: '#F9FAFB',
        sectionLabel: '#9CA3AF',
        isInverted: false,
      };
  }
}

// Default palette switched 'etg' → 'kova' to align the product with the
// AnooreHR marketing site (anoorehr.com). The 'etg' palette remains available
// as a per-tenant override for legacy customers who selected the blue/red look.
export const DEFAULT_PALETTE_KEY = 'kova';
export const DEFAULT_PALETTE = COLOR_PALETTES[DEFAULT_PALETTE_KEY];

/**
 * Get palette by key with fallback to default
 */
export function getPaletteByKey(key?: string | null): ColorPalette {
  if (!key || !COLOR_PALETTES[key]) {
    return DEFAULT_PALETTE;
  }
  return COLOR_PALETTES[key];
}

/**
 * List all available palettes for UI dropdown
 */
export function listAvailablePalettes() {
  return Object.entries(COLOR_PALETTES).map(([key, palette]) => ({
    key,
    name: palette.name,
    description: palette.description,
    primary: palette.primary,
    primaryDark: palette.primaryDark,
    primaryLight: palette.primaryLight,
    secondary: palette.secondary,
    success: palette.success,
    error: palette.error,
    warning: palette.warning,
    info: palette.info,
    background: palette.background,
  }));
}
